import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import { useAuth } from '../../context/AuthContext.jsx'
import api from '../../lib/api.js'
import { passwordChecks, isValidPassword, passwordScore } from '../../lib/password.js'

/*
 * My profile + password change — inventory §5 Users tab (personal part).
 * Profile: first/last/email/language + notify_on_incoming_invoice.
 * Password: current + new + confirm, validated against the policy.
 */
const LANGS = ['fr', 'en', 'nl']

export default function ProfileTab() {
  const { t, i18n } = useTranslation()
  const { user, setUser, fetchProfile } = useAuth()
  const { toast, notify } = useFeedback()

  const [form, setForm] = useState({
    first_name: user?.first_name || '', last_name: user?.last_name || '', email: user?.email || '',
    preferred_language: user?.preferred_language || 'fr', notify_on_incoming_invoice: !!user?.notify_on_incoming_invoice,
  })
  const [savingProfile, setSavingProfile] = useState(false)
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const saveProfile = async () => {
    setSavingProfile(true)
    try {
      await api.put('/auth/profile', form)
      if (form.preferred_language && form.preferred_language !== i18n.resolvedLanguage) i18n.changeLanguage(form.preferred_language)
      const p = await fetchProfile(); if (p?.user) setUser(p.user)
      toast(t('settings.profile.saved'), 'success')
    } catch (e) { notify({ tone: 'error', title: t('settings.error'), message: e?.response?.data?.error || t('settings.errorGeneric') }) }
    finally { setSavingProfile(false) }
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18, alignItems: 'start' }}>
      <Card>
        <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, marginBottom: 16 }}>{t('settings.profile.title')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Field label={t('settings.profile.firstName')}><Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} /></Field>
          <Field label={t('settings.profile.lastName')}><Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} /></Field>
        </div>
        <Field label={t('parties.fields.email')}><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field label={t('parties.fields.language')}>
          <div style={{ display: 'flex', gap: 8 }}>{LANGS.map((l) => (
            <button key={l} type="button" onClick={() => set('preferred_language', l)} style={{ flex: 1, height: 40, borderRadius: 'var(--radius-field)', fontSize: 13, fontWeight: 600, border: '1.5px solid ' + (form.preferred_language === l ? 'var(--color-primary)' : 'var(--color-border-strong)'), background: form.preferred_language === l ? 'var(--color-bg-lilac-strong)' : '#fff', color: form.preferred_language === l ? 'var(--color-primary)' : 'var(--color-text)' }}>{l.toUpperCase()}</button>
          ))}</div>
        </Field>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, marginTop: 6 }}>
          <input type="checkbox" checked={form.notify_on_incoming_invoice} onChange={(e) => set('notify_on_incoming_invoice', e.target.checked)} />
          {t('settings.profile.notifyIncoming')}
        </label>
        <div style={{ marginTop: 18 }}><Button variant="primary" icon="check" loading={savingProfile} onClick={saveProfile}>{t('common.save')}</Button></div>
      </Card>

      <PasswordCard t={t} toast={toast} notify={notify} />
    </div>
  )
}

function PasswordCard({ t, toast, notify }) {
  const [pw, setPw] = useState({ current_password: '', new_password: '', confirm_password: '' })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setPw((s) => ({ ...s, [k]: v }))
  const checks = passwordChecks(pw.new_password)
  const score = passwordScore(pw.new_password)
  const mismatch = pw.confirm_password && pw.new_password !== pw.confirm_password

  const submit = async (e) => {
    e.preventDefault()
    if (!isValidPassword(pw.new_password)) { notify({ tone: 'error', title: t('settings.error'), message: t('settings.password.weak') }); return }
    if (pw.new_password !== pw.confirm_password) { notify({ tone: 'error', title: t('settings.error'), message: t('settings.password.mismatch') }); return }
    setSaving(true)
    try {
      await api.put('/auth/password', pw)
      toast(t('settings.password.saved'), 'success')
      setPw({ current_password: '', new_password: '', confirm_password: '' })
    } catch (e2) { notify({ tone: 'error', title: t('settings.error'), message: e2?.response?.data?.error || t('settings.password.failed') }) }
    finally { setSaving(false) }
  }

  const barColor = ['#e6e1f2', 'var(--color-danger)', 'var(--color-accent)', 'var(--color-success)'][score]

  return (
    <Card>
      <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, marginBottom: 16 }}>{t('settings.password.title')}</h3>
      <form onSubmit={submit}>
        <Field label={t('settings.password.current')}><Input type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => set('current_password', e.target.value)} required /></Field>
        <Field label={t('settings.password.new')}><Input type="password" autoComplete="new-password" value={pw.new_password} onChange={(e) => set('new_password', e.target.value)} required /></Field>
        {pw.new_password && (
          <div style={{ marginTop: -8, marginBottom: 14 }}>
            <div style={{ height: 5, borderRadius: 3, background: '#eee', overflow: 'hidden', marginBottom: 8 }}>
              <div style={{ height: '100%', width: `${(score / 3) * 100}%`, background: barColor, transition: 'width .2s' }} />
            </div>
            <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
              {[['length', t('settings.password.ruleLength')], ['digit', t('settings.password.ruleDigit')], ['special', t('settings.password.ruleSpecial')]].map(([k, label]) => (
                <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: checks[k] ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                  <Icon name={checks[k] ? 'check' : 'x'} size={13} /> {label}
                </span>
              ))}
            </div>
          </div>
        )}
        <Field label={t('settings.password.confirm')} error={mismatch ? t('settings.password.mismatch') : undefined}>
          <Input type="password" autoComplete="new-password" value={pw.confirm_password} onChange={(e) => set('confirm_password', e.target.value)} required style={mismatch ? { borderColor: 'var(--color-danger)' } : undefined} />
        </Field>
        <div style={{ marginTop: 6 }}><Button type="submit" variant="primary" icon="check" loading={saving}>{t('settings.password.change')}</Button></div>
      </form>
    </Card>
  )
}
